import { Group, Matrix4 } from 'three'
import type { ITileContentAdapter } from '@/plugins/tiles/contracts'
import type { RuntimeTileNode } from '@/plugins/tiles/hierarchy/ExplicitHierarchyProvider'
import { selectCollisionTiles } from '@/plugins/tiles/runtime/TileCollisionSelector'
import type { TileSelectionRequest } from '@/plugins/tiles/runtime/TileSelector'
import type { TilesRuntime } from '@/plugins/tiles/runtime/TilesRuntime'
import type {
  TilesCollisionCoverage,
  TilesCollisionInterest,
  TilesCollisionOptions,
  TilesetHandle,
} from '@/plugins/tiles/types'
import { getLoggerManager } from '@/shared/logging'
import { validateCollisionTransform } from '@/shared/spatial/validateCollisionTransform'
import type { SpatialQueryService } from '@/shared/types/services'
import type { SpatialWorldHandle } from '@/shared/types/spatial'

interface Consumer {
  interest: TilesCollisionInterest
  coverage: TilesCollisionCoverage
  committed: readonly string[]
}
const log = getLoggerManager().getLogger('tiles:collision')

/** Record 自有空间桥；最终 world 由同一 Tiles 缓存包管理。 */
export class TilesCollisionRuntime {
  private readonly _consumers = new Map<string, Consumer>()
  private readonly _worlds = new Map<string, SpatialWorldHandle>()
  private readonly _building = new Map<string, AbortController>()
  private readonly _errors = new Map<string, string>()
  private readonly _budgetFailures = new Set<string>()
  private readonly _pins = new Set<string>()
  private readonly _buildCosts = new Map<string, number>()
  private _bindings: readonly string[] = []
  private _bindingKey = ''
  private _disposed = false
  private _generation = 0
  private _active = false
  private _placementKey = ''

  constructor(
    private readonly _handle: TilesetHandle,
    private readonly _runtime: TilesRuntime,
    private readonly _adapter: ITileContentAdapter,
    private readonly _spatial: SpatialQueryService,
    readonly options: TilesCollisionOptions,
    private readonly _invalidate: () => void
  ) {
    if (options.mode === 'streaming') {
      for (const [key, value] of Object.entries({
        maxGeometricError: options.maxGeometricError,
        maxBytes: options.maxBytes,
        maxInterestRadius: options.maxInterestRadius ?? 100,
        maxConcurrentBuilds: options.maxConcurrentBuilds ?? 1,
      }))
        if (!Number.isFinite(value) || value <= 0) throw new Error(`无效碰撞配置: ${key}`)
      if (!Number.isInteger(options.maxConcurrentBuilds ?? 1))
        throw new Error('碰撞构建并发必须为整数')
      if (!Number.isFinite(options.prefetchDistance ?? 2) || (options.prefetchDistance ?? 2) < 0)
        throw new Error('碰撞预取范围必须有限且非负')
      if (!_adapter.createCollisionGeometry) throw new Error('内容适配器不支持碰撞几何')
    }
  }

  get hasDemand(): boolean {
    return this._consumers.size > 0 || this.options.mode === 'proxy'
  }

  setInterest(consumerId: string, interest: TilesCollisionInterest): TilesCollisionCoverage {
    if (this._disposed) throw new Error('Tiles 碰撞已销毁')
    const values = [
      ...interest.bounds.min.toArray(),
      ...interest.bounds.max.toArray(),
      ...(interest.motion?.toArray() ?? []),
    ]
    const maxRadius =
      this.options.mode === 'streaming' ? (this.options.maxInterestRadius ?? 100) : 100
    const effectiveBounds = interest.bounds
      .clone()
      .expandByScalar(this.options.mode === 'streaming' ? (this.options.prefetchDistance ?? 2) : 0)
    if (interest.motion) effectiveBounds.union(effectiveBounds.clone().translate(interest.motion))
    if (
      !consumerId ||
      interest.bounds.isEmpty() ||
      !values.every(Number.isFinite) ||
      effectiveBounds.min.distanceTo(effectiveBounds.max) > maxRadius * 2
    )
      throw new Error('碰撞兴趣区域必须非空、有限且不超过配置范围')
    const current = this._consumers.get(consumerId)
    if (
      current?.interest.bounds.equals(interest.bounds) &&
      (current.interest.motion && interest.motion
        ? current.interest.motion.equals(interest.motion)
        : current.interest.motion === interest.motion)
    )
      return this.getCoverage(consumerId)!
    const bounds = interest.bounds.clone()
    this._consumers.set(consumerId, {
      interest: { bounds, motion: interest.motion?.clone() },
      committed: current?.committed ?? [],
      coverage: { consumerId, generation: ++this._generation, bounds, state: 'loading' },
    })
    this._invalidate()
    return this.getCoverage(consumerId)!
  }

  removeInterest(id: string): void {
    this._consumers.delete(id)
    this._invalidate()
  }

  getCoverage(id: string): TilesCollisionCoverage | undefined {
    this._handle.object3D.updateWorldMatrix(true, false)
    const key = this._handle.object3D.matrixWorld.elements.join(',')
    if (this._placementKey && key !== this._placementKey) this.invalidateTransform()
    this._placementKey = key
    const state = this._consumers.get(id)?.coverage
    return state ? Object.freeze({ ...state, bounds: state.bounds.clone() }) : undefined
  }

  invalidateTransform(): void {
    this.releaseBindings()
    for (const consumer of this._consumers.values()) {
      consumer.committed = []
      consumer.coverage = {
        ...consumer.coverage,
        generation: ++this._generation,
        state: this._active ? 'loading' : 'inactive',
        reason: 'owner transform changed',
      }
    }
  }

  setActive(active: boolean): void {
    if (this._disposed || this._active === active) return
    this._active = active
    if (!active) {
      this.releaseBindings()
      for (const controller of this._building.values()) controller.abort()
      for (const consumer of this._consumers.values()) {
        consumer.committed = []
        consumer.coverage = { ...consumer.coverage, state: 'inactive', reason: 'owner inactive' }
      }
      this._runtime.setCollisionRequests([])
    }
  }

  update(): void {
    if (!this._active || this._disposed) return
    this._handle.object3D.updateWorldMatrix(true, false)
    const placement = this._handle.object3D.matrixWorld
    const placementKey = placement.elements.join(',')
    if (this._placementKey && placementKey !== this._placementKey) this.invalidateTransform()
    this._placementKey = placementKey
    try {
      validateCollisionTransform(placement)
    } catch (error) {
      this.releaseBindings()
      this._runtime.setCollisionRequests([])
      for (const consumer of this._consumers.values())
        consumer.coverage = { ...consumer.coverage, state: 'degraded', reason: String(error) }
      return
    }
    if (this.options.mode === 'proxy') {
      if (!this._bindings.length)
        this._bindings = [
          this._spatial.bindWorld({
            worldId: this.options.worldId,
            ownerAssetId: this._handle.id,
            ownerKind: 'tiles',
            representationRoles: ['collision'],
            ownerStatePolicy: this.options.ownerStatePolicy,
          }).id,
        ]
      const ready =
        this._spatial.getWorld(this.options.worldId)?.getRepresentation('collision').state ===
        'ready'
      for (const consumer of this._consumers.values())
        consumer.coverage = { ...consumer.coverage, state: ready ? 'ready' : 'loading' }
      return
    }
    const requests: TileSelectionRequest[] = []
    const retained = new Set<string>()
    for (const consumer of this._consumers.values()) {
      const bounds = consumer.interest.bounds
        .clone()
        .expandByScalar(this.options.prefetchDistance ?? 2)
      if (consumer.interest.motion) bounds.union(bounds.clone().translate(consumer.interest.motion))
      const selected = selectCollisionTiles(
        this._runtime.hierarchy.root,
        bounds,
        placement,
        this.options.maxGeometricError
      )
      requests.push(...selected.requests)
      let ready = !selected.unknown
      let reason = selected.requests
        .map(({ node }) => this._runtime.getCollisionFailure(node))
        .find(Boolean)
      for (const node of selected.nodes) {
        retained.add(node.id)
        if (this._worlds.has(node.id)) {
          if (
            !this._pins.has(node.id) &&
            this._runtime.cache.pin(this.cacheId(node.id), 'collision')
          )
            this._pins.add(node.id)
          continue
        }
        ready = false
        const content = this._runtime.getCollisionContent(node)
        if (!content) requests.push({ node, kind: 'content', priority: 100, purpose: 'collision' })
        else this.build(node)
        reason = this._errors.get(node.id) ?? this._runtime.getCollisionFailure(node) ?? reason
      }
      if (selected.sourceLimited) {
        ready = false
        reason = '源叶几何误差高于碰撞目标'
      }
      if (ready) consumer.committed = selected.nodes.map((node) => node.id)
      for (const id of consumer.committed) retained.add(id)
      consumer.coverage = {
        ...consumer.coverage,
        state: selected.sourceLimited
          ? 'source-limit'
          : reason
            ? 'degraded'
            : ready
              ? 'ready'
              : 'loading',
        reason,
      }
    }
    this._runtime.setCollisionRequests(requests)
    const committed = [
      ...new Set([...this._consumers.values()].flatMap((consumer) => consumer.committed)),
    ]
    const key = committed.sort().join('|')
    if (key !== this._bindingKey) {
      this._bindings = this._spatial
        .replaceCollisionBindings(
          this._bindings,
          committed.map((id) => ({
            worldId: this._worlds.get(id)!.id,
            ownerAssetId: this._handle.id,
            ownerKind: 'tiles',
            representationRoles: ['collision'],
            ownerStatePolicy: this.options.ownerStatePolicy,
          }))
        )
        .map((binding) => binding.id)
      this._bindingKey = key
    }
    for (const id of [...this._pins])
      if (!retained.has(id)) {
        this._runtime.cache.unpin(this.cacheId(id), 'collision')
        this._pins.delete(id)
        this._runtime.cache.releaseUnprotected(this.cacheId(id))
      }
    for (const [id, controller] of this._building) if (!retained.has(id)) controller.abort()
  }

  retryFailed(): void {
    if (this._disposed) return
    this._errors.clear()
    this._budgetFailures.clear()
    if (this._active) this._invalidate()
  }

  private build(node: RuntimeTileNode): void {
    if (
      this.options.mode !== 'streaming' ||
      this._building.has(node.id) ||
      this._building.size >= (this.options.maxConcurrentBuilds ?? 1)
    )
      return
    if (this._errors.has(node.id) && !this._budgetFailures.has(node.id)) return
    const content = this._runtime.getCollisionContent(node)
    if (!content) return
    // 覆盖 POSITION 展开、归一化复制、索引/BVH/元数据及构建峰值的软件预约。
    const bytes = Math.max(1024, content.costs.geometryBytes * 24)
    const current = [...this._buildCosts.values()].reduce((sum, value) => sum + value, 0)
    if (
      current + bytes > this.options.maxBytes ||
      !this._runtime.cache.reserve(this.cacheId(node.id), bytes).accepted
    ) {
      this._errors.set(node.id, '碰撞子预算或 Record 总预算不足')
      this._budgetFailures.add(node.id)
      return
    }
    const controller = new AbortController()
    this._building.set(node.id, controller)
    this._buildCosts.set(node.id, bytes)
    this._errors.delete(node.id)
    this._budgetFailures.delete(node.id)
    const sourceId =
      this._runtime.cache.peek(node.id) === content ? node.id : `${node.id}#collision`
    const lease = this._runtime.registry.retain(sourceId)
    const execute = async () => {
      let geometry:
        | ReturnType<NonNullable<ITileContentAdapter['createCollisionGeometry']>>
        | undefined
      let world: SpatialWorldHandle | undefined
      try {
        geometry = this._adapter.createCollisionGeometry!({
          content,
          matrixWorld: new Matrix4().multiplyMatrices(node.sourceToRecord, node.tileMatrix),
        })
        world = await this._spatial.prepareCollisionGeometry(geometry, controller.signal)
        if (this._disposed || !this._active || controller.signal.aborted) {
          world.dispose()
          return
        }
        const result = this._runtime.cache.admit(this.cacheId(node.id), {
          adapterId: 'collision-world',
          source: content.source,
          object3D: new Group(),
          boundingBox: content.boundingBox.clone(),
          costs: { inputBytes: 0, geometryBytes: bytes, textureBytes: 0, transientBytes: 0 },
          dispose: () => {
            this._worlds.delete(node.id)
            this._buildCosts.delete(node.id)
            world!.dispose()
          },
        })
        // cache.admit 无论接受/拒绝都接管 world 最终释放。
        if (result.accepted) {
          this._worlds.set(node.id, world)
          this._runtime.cache.pin(this.cacheId(node.id), 'collision')
          this._pins.add(node.id)
        } else {
          this._errors.set(node.id, `碰撞表示准入失败: ${result.reason}`)
          if (result.reason === 'oversized' || result.reason === 'protected-budget')
            this._budgetFailures.add(node.id)
        }
      } catch (error) {
        if (!controller.signal.aborted && !this._disposed) this._errors.set(node.id, String(error))
      } finally {
        geometry?.dispose()
        lease?.release()
        this._runtime.cache.cancelReservation(this.cacheId(node.id))
        this._building.delete(node.id)
        if (!this._worlds.has(node.id)) this._buildCosts.delete(node.id)
        if (!this._disposed) this._invalidate()
      }
    }
    void execute()
  }

  dispose(): void {
    if (this._disposed) return
    this.setActive(false)
    this._disposed = true
    this._consumers.clear()
  }

  private cacheId(id: string): string {
    return `${id}#bvh`
  }
  private releaseBindings(): void {
    try {
      this._spatial.replaceCollisionBindings(this._bindings, [])
    } catch (error) {
      log.warn(`解除碰撞集合失败，逐项清理: ${String(error)}`)
      for (const id of this._bindings) {
        try {
          this._spatial.unbindWorld(id)
        } catch (itemError) {
          log.warn(`解除碰撞绑定失败: ${String(itemError)}`)
        }
      }
    }
    this._bindings = []
    this._bindingKey = ''
    for (const id of this._pins) {
      this._runtime.cache.unpin(this.cacheId(id), 'collision')
      this._runtime.cache.releaseUnprotected(this.cacheId(id))
    }
    this._pins.clear()
  }
}
